"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import type { useGameAnalysisAuthorization } from "./useGameAnalysisAuthorization";

function rowPosition(control: HTMLButtonElement) {
  const row = control.closest("tr");
  return { row, index: row ? Array.from(row.parentElement?.children ?? []).indexOf(row) : 0 };
}

/** Preserve the originating control's focus intent across native disabled-button blur. */
export function useGameAnalysisRowFocus(selected: boolean, access: ReturnType<typeof useGameAnalysisAuthorization>) {
  const tableHost = useRef<HTMLDivElement>(null);
  const titleFilterButton = useRef<HTMLButtonElement>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const focusVersion = useRef(0);
  const modalIntent = useRef<{ control: HTMLButtonElement; cancelled: boolean }>();
  const request = useRef<{ control: HTMLButtonElement; index: number }>();
  const focusRemaining = (index: number) => {
    const remaining = Array.from(tableHost.current?.querySelectorAll<HTMLButtonElement>("tbody tr td:nth-child(2) button") ?? []);
    (remaining[Math.min(index, remaining.length - 1)] ?? titleFilterButton.current)?.focus();
  };
  useEffect(() => {
    const moved = (event: FocusEvent) => {
      focusVersion.current += 1;
      const intent = modalIntent.current;
      const target = event.target;
      if (intent && target instanceof Element && target !== document.body && target !== intent.control && !target.closest("[data-user-comment-editor]")) intent.cancelled = true;
    };
    document.addEventListener("focusin", moved);
    return () => document.removeEventListener("focusin", moved);
  }, []);
  useLayoutEffect(() => {
    const target = request.current;
    request.current = undefined;
    if (!target || target.control.isConnected || !selected || !access.isCurrent()) return;
    focusRemaining(target.index);
  });
  const trackRemoval = (control: HTMLButtonElement) => {
    const { index } = rowPosition(control);
    const startedFocused = document.activeElement === control;
    const version = focusVersion.current;
    return () => {
      if (document.activeElement === control || (startedFocused && focusVersion.current === version && document.activeElement === document.body)) {
        request.current = { control, index };
      }
    };
  };
  // Modal focus movement belongs to the edit intent rather than superseding it.
  const trackModal = (control: HTMLButtonElement) => {
    const intent = { control, cancelled: false };
    modalIntent.current = intent;
    const { row, index } = rowPosition(control);
    const gameId = row?.dataset.gameAnalysisId;
    return () => {
      if (modalIntent.current !== intent) return;
      modalIntent.current = undefined;
      if (intent.cancelled) return;
      const active = document.activeElement;
      if (active !== document.body && active !== control) return;
      if (!selectedRef.current || !access.isCurrent()) return;
      const currentRow = row?.isConnected ? row : gameId ? tableHost.current?.querySelector<HTMLTableRowElement>(`tr[data-game-analysis-id="${gameId}"]`) : undefined;
      const currentControl = control.isConnected ? control : currentRow?.querySelector<HTMLButtonElement>("[data-user-comment-entry]");
      if (currentControl) {
        (currentControl.disabled ? currentRow?.querySelector<HTMLButtonElement>("td:nth-child(2) button") : currentControl)?.focus();
      } else focusRemaining(index);
    };
  };
  return { tableHost, titleFilterButton, trackRemoval, trackModal };
}
